/**
 * Algolia QueryID Storage Utility
 *
 * Stores queryID in localStorage per product to:
 * 1. Keep URLs clean for SEO (no ?queryID= parameter)
 * 2. Maintain analytics tracking for search attribution
 * 3. Handle cross-tab navigation (right-click → new tab, ctrl+click)
 *
 * Uses onMouseEnter to capture queryID BEFORE any click action,
 * ensuring right-click → open in new tab also works.
 *
 * localStorage is used (instead of sessionStorage) to share data across tabs.
 * 30-minute expiry prevents stale queryID attribution.
 */

const STORAGE_KEY_PREFIX = 'algolia_queryID_';
const LAST_CLEANUP_KEY = 'algolia_queryID_lastCleanup';
const MAX_STORED_ITEMS = 50;
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

export interface StoredQueryIDData {
  queryID: string;
  position: number;
  indexName: string;
  timestamp: number;
}

/**
 * Remove expired entries to prevent localStorage bloat.
 * Keeps only the most recent MAX_STORED_ITEMS entries.
 */
const cleanupExpiredEntries = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Cleanup old entries to prevent storage bloat
    // Throttle cleanup: only run once every 60 seconds to avoid performance impact
    const lastCleanup = Number.parseInt(localStorage.getItem(LAST_CLEANUP_KEY) || '0', 10);
    if (Date.now() - lastCleanup < 60 * 1000) {
      return;
    }
    localStorage.setItem(LAST_CLEANUP_KEY, Date.now().toString());

    const keysToRemove: string[] = [];
    const validEntries: Array<{ key: string; timestamp: number }> = [];

    // Iterate through localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) {
        continue;
      }

      try {
        const stored = localStorage.getItem(key);
        if (!stored) {
          continue;
        }

        const data: StoredQueryIDData = JSON.parse(stored);

        // Mark expired entries for removal
        if (Date.now() - data.timestamp > EXPIRY_MS) {
          keysToRemove.push(key);
        } else {
          validEntries.push({ key, timestamp: data.timestamp });
        }
      } catch {
        // Invalid JSON, remove it
        keysToRemove.push(key);
      }
    }

    // If still too many valid entries, add oldest ones to removal list
    if (validEntries.length > MAX_STORED_ITEMS) {
      validEntries
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(0, validEntries.length - MAX_STORED_ITEMS)
        .forEach(entry => keysToRemove.push(entry.key));
    }

    // Remove all collected keys after iteration completes
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore cleanup errors
  }
};

/**
 * Store queryID for a specific product.
 * Called on onMouseEnter to capture before any click type.
 *
 * @param objectID - The product item code
 * @param queryID - Algolia queryID from search response
 * @param position - Position of item in search results
 * @param indexName - Algolia index name
 */
export const storeQueryIDForProduct = (
  objectID: string,
  queryID: string | undefined,
  position = 0,
  indexName = '',
): void => {
  if (typeof window === 'undefined' || !queryID || !objectID) {
    return;
  }

  try {
    const data: StoredQueryIDData = {
      queryID,
      position,
      indexName,
      timestamp: Date.now(),
    };

    localStorage.setItem(`${STORAGE_KEY_PREFIX}${objectID}`, JSON.stringify(data));

    cleanupExpiredEntries();
  } catch {
    // localStorage may be unavailable (private browsing, storage full, etc.)
  }
};

/**
 * Retrieve stored queryID for a specific product.
 * Returns null if not found or expired.
 *
 * @param objectID - The product item code
 * @returns StoredQueryIDData or null
 */
export const getQueryIDForProduct = (objectID: string): StoredQueryIDData | null => {
  if (typeof window === 'undefined' || !objectID) {
    return null;
  }

  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${objectID}`);
    if (!stored) {
      return null;
    }

    const data: StoredQueryIDData = JSON.parse(stored);

    // Check if expired (30 minutes)
    if (Date.now() - data.timestamp > EXPIRY_MS) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${objectID}`);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

/**
 * Get just the queryID string for a product.
 * Convenience function for analytics tracking.
 *
 * @param objectID - The product item code
 * @param fallbackQueryID - Optional fallback if not in storage
 * @returns queryID string or empty string
 */
export const getQueryIDStringForProduct = (objectID: string, fallbackQueryID?: string): string => {
  const stored = getQueryIDForProduct(objectID);
  return stored?.queryID || fallbackQueryID || '';
};

/**
 * Clear all stored queryIDs.
 * Useful for testing or when user logs out.
 */
export const clearAllStoredQueryIDs = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
};

/**
 * Clear stored queryID for a specific product.
 *
 * @param objectID - The product item code
 */
export const clearQueryIDForProduct = (objectID: string): void => {
  if (typeof window === 'undefined' || !objectID) {
    return;
  }

  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${objectID}`);
  } catch {
    // Ignore errors
  }
};
